import { Box, Collapse } from '@ltvco/refresh-lib/theme';
import BusinessIcon from '@mui/icons-material/Business';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EmailIcon from '@mui/icons-material/Email';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIcon from '@mui/icons-material/Phone';
import SchoolIcon from '@mui/icons-material/School';
import { useState } from 'react';

import {
  AccordionCard,
  AccordionCardButton,
  AccordionDetailList,
  Chevron,
  CompactReportFieldRow,
  ContactRecordItem,
  ContactRecordList,
  ContactRecordSub,
  ContactRecordTitle,
  ExpandPanelInner,
  ExpandRowCount,
  ExpandRowLabel,
  ExpandRowMain,
  ExpandRowPreview,
  FieldList,
  ItemTextStack,
  OwnerAvatar,
  OwnerCard,
  OwnerCardContent,
  OwnerCardHeader,
  OwnerCardName,
  PreviewCardSection,
  RecordItem,
  StatusFieldRow,
  OwnerReportBar,
  OwnerReportButton,
  OwnerReportText,
} from '../../components';
import {
  formatPhoneLinkType,
  formatPhoneNumber,
  getInitials,
  titleCase,
  type StatusSeverity,
} from '../utils';

import { LinkedSection } from './components';

import type {
  EmailContactAddress,
  EmailContactEmail,
  EmailContactPhone,
  EmailJob,
  EmailPotentialOwner,
  EmailReportData,
  EmailSocialProfile,
} from 'components/DeepSearch/types';

// ─── helpers ──────────────────────────────────────────────────────────────────

const contactIconSx = { fontSize: 18, color: 'text.secondary', flexShrink: 0 };

function getSpamTrapSeverity(value: string): StatusSeverity {
  const v = value.trim().toLowerCase();
  if (v === 'none' || v === 'low') {
    return 'success';
  }
  if (v === 'medium') {
    return 'warning';
  }
  return 'error';
}

function getDeliverabilityScore(value: string): StatusSeverity {
  const v = value.trim().toLowerCase();
  if (v === 'high') {
    return 'success';
  }
  if (v === 'medium') {
    return 'warning';
  }
  return 'error';
}

// ─── EmailFraudScanSection ────────────────────────────────────────────────────

export const EmailFraudScanSection = ({ data }: { data: EmailReportData }) => {
  const items: Array<{
    label: string;
    value: string;
    severity: StatusSeverity;
    info: string;
    infoLabel: string;
  }> = [
    {
      label: 'Valid email',
      value: data.validEmail,
      severity:
        data.validEmail.trim().toLowerCase() === 'yes' ? 'success' : 'error',
      info: 'This address is correctly formatted and active.',
      infoLabel: 'What does valid email mean?',
    },
    {
      label: 'Leaked in data breach',
      value: data.isLeaked,
      severity:
        data.isLeaked.trim().toLowerCase() === 'yes' ? 'error' : 'success',
      info:
        data.isLeaked.trim().toLowerCase() === 'yes'
          ? 'Found in a known public data breach.'
          : 'Not found in any known data breach.',
      infoLabel: 'What does leaked in data breach mean?',
    },
    {
      label: 'Frequent complaint history',
      value: data.isFrequentComplainer,
      severity:
        data.isFrequentComplainer.trim().toLowerCase() === 'yes'
          ? 'error'
          : 'success',
      info:
        data.isFrequentComplainer.trim().toLowerCase() === 'yes'
          ? 'This address has a frequent history of spam complaints.'
          : 'Few or no spam complaints are tied to this address.',
      infoLabel: 'What is complaint history?',
    },
    {
      label: 'Recent abuse',
      value: data.recentAbuse,
      severity:
        data.recentAbuse.trim().toLowerCase() === 'yes' ? 'error' : 'success',
      info:
        data.recentAbuse.trim().toLowerCase() === 'yes'
          ? 'Recent reports of fraudulent or abusive activity.'
          : 'No recent reports of fraudulent or abusive activity.',
      infoLabel: 'What does recent abuse mean?',
    },
    {
      label: 'Spam trap score',
      value: data.spamTrapScore,
      severity: getSpamTrapSeverity(data.spamTrapScore),
      info: 'Likelihood this address is a spam trap.',
      infoLabel: 'What is a spam trap?',
    },
    {
      label: 'Deliverability',
      value: data.deliverability,
      severity: getDeliverabilityScore(data.deliverability),
      info: 'How likely mail to this address is delivered.',
      infoLabel: 'What is deliverability?',
    },
  ];

  return (
    <PreviewCardSection title="Fraud scan">
      <FieldList>
        {items.map((item) => (
          <StatusFieldRow key={item.label} {...item} />
        ))}
      </FieldList>
    </PreviewCardSection>
  );
};

// ─── EmailPotentialOwnersSection ──────────────────────────────────────────────

const OwnerItem = ({
  owner,
  onSendMessage,
}: {
  owner: EmailPotentialOwner;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const hasContent =
    !!owner.primaryAddress || !!owner.primaryPhone || !!owner.primaryEmail;
  const isExpandable = hasContent || !!onSendMessage;
  const toggleOpen = () => setOpen((open) => !open);

  function handleSendMessage() {
    if (!onSendMessage) {
      return;
    }
    const content = owner.bvId
      ? `Run a person report on ${owner.name}, bv_id: ${owner.bvId}`
      : `Run a person report on ${owner.name}`;
    onSendMessage(content, `Search for ${owner.name}`);
  }

  return (
    <OwnerCard>
      <OwnerCardHeader
        type="button"
        aria-expanded={isExpandable ? open : undefined}
        disabled={!isExpandable}
        onClick={isExpandable ? toggleOpen : undefined}
      >
        <OwnerAvatar>{getInitials(owner.name)}</OwnerAvatar>
        <OwnerCardName>{owner.name}</OwnerCardName>
        {isExpandable && <Chevron open={open} />}
      </OwnerCardHeader>
      {isExpandable && (
        <Collapse in={open}>
          <OwnerCardContent>
            {onSendMessage && (
              <OwnerReportBar type="button" onClick={handleSendMessage}>
                <OwnerReportText>
                  View a complete report for {owner.name}
                </OwnerReportText>
                <OwnerReportButton>
                  <ChevronRightIcon sx={{ fontSize: 20 }} />
                </OwnerReportButton>
              </OwnerReportBar>
            )}
            {hasContent && (
              <AccordionDetailList>
                {owner.primaryAddress && (
                  <CompactReportFieldRow
                    label="Primary address"
                    value={owner.primaryAddress}
                  />
                )}
                {owner.primaryPhone && (
                  <CompactReportFieldRow
                    label="Primary phone"
                    value={formatPhoneNumber(owner.primaryPhone)}
                  />
                )}
                {owner.primaryEmail && (
                  <CompactReportFieldRow
                    label="Primary email"
                    value={owner.primaryEmail}
                  />
                )}
              </AccordionDetailList>
            )}
          </OwnerCardContent>
        </Collapse>
      )}
    </OwnerCard>
  );
};

export const EmailPotentialOwnersSection = ({
  owner,
  onSendMessage,
}: {
  owner: EmailPotentialOwner;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => (
  <PreviewCardSection title="Possible owner(s)">
    <OwnerItem owner={owner} onSendMessage={onSendMessage} />
  </PreviewCardSection>
);

// ─── Linked records sub-rows ──────────────────────────────────────────────────

const AddressRows = ({
  addresses,
  onSendMessage,
}: {
  addresses: EmailContactAddress[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (addresses.length === 0) {
    return null;
  }
  const preview = addresses
    .slice(0, 2)
    .map((a) => a.full.split(' ').slice(-2).join(', '))
    .join(' · ');
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible addresses</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={addresses.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {addresses.map((addr, i) => (
              <ContactRecordItem
                key={i}
                icon={<LocationOnIcon sx={contactIconSx} />}
                title={addr.full}
                sub={i === 0 ? 'Primary address' : undefined}
                onClick={
                  onSendMessage
                    ? () => {
                        const msg = `Property report for ${addr.full}`;
                        onSendMessage(msg, msg);
                      }
                    : undefined
                }
              />
            ))}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const EmailRows = ({
  emails,
  currentEmail,
  onSendMessage,
}: {
  emails: EmailContactEmail[];
  currentEmail: string;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (emails.length === 0) {
    return null;
  }
  const preview = emails[0]?.address ?? '';
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible emails</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={emails.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {emails.map((email, i) => {
              const typeLabel = email.type
                ? titleCase(email.type.replace(/_/g, ' '))
                : '';
              const isThis =
                email.address.toLowerCase() === currentEmail.toLowerCase();
              const qualifier = isThis
                ? 'this email'
                : i === 0
                ? 'primary'
                : '';
              const sub =
                [typeLabel, qualifier].filter(Boolean).join(' · ') || undefined;
              return (
                <ContactRecordItem
                  key={email.address}
                  icon={<EmailIcon sx={contactIconSx} />}
                  title={email.address}
                  sub={sub}
                  onClick={
                    onSendMessage && !isThis
                      ? () => {
                          const msg = `Email report for ${email.address}`;
                          onSendMessage(msg, msg);
                        }
                      : undefined
                  }
                />
              );
            })}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const PhoneRows = ({
  phones,
  onSendMessage,
}: {
  phones: EmailContactPhone[];
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (phones.length === 0) {
    return null;
  }
  const preview = phones[0]?.number ? formatPhoneNumber(phones[0].number) : '';
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Possible phone numbers</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={phones.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {phones.map((phone) => {
              const formatted = formatPhoneNumber(phone.number);
              return (
                <ContactRecordItem
                  key={phone.number}
                  icon={<PhoneIcon sx={contactIconSx} />}
                  title={formatted}
                  sub={formatPhoneLinkType(phone.type) || undefined}
                  onClick={
                    onSendMessage
                      ? () => {
                          const msg = `Phone report for ${formatted}`;
                          onSendMessage(msg, msg);
                        }
                      : undefined
                  }
                />
              );
            })}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const SocialRow = ({ profiles }: { profiles: EmailSocialProfile[] }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (profiles.length === 0) {
    return null;
  }
  const preview = profiles
    .slice(0, 2)
    .map((p) => p.platform)
    .join(' · ');
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Social profiles</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={profiles.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {profiles.map((profile, i) => {
              let safeUrl = '#';
              try {
                const parsed = new URL(profile.url);
                if (
                  parsed.protocol === 'https:' ||
                  parsed.protocol === 'http:'
                ) {
                  safeUrl = profile.url;
                }
              } catch {
                // invalid URL — keep '#'
              }
              return (
                <RecordItem key={i} surface="paper">
                  <a
                    href={safeUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    <span
                      style={{
                        display: 'flex',
                        gap: 10,
                        alignItems: 'center',
                      }}
                    >
                      <LinkIcon sx={contactIconSx} />
                      <ItemTextStack>
                        <ContactRecordTitle>
                          {profile.platform}
                        </ContactRecordTitle>
                        {profile.handle && (
                          <ContactRecordSub sx={{ color: 'primary.main' }}>
                            {profile.handle}
                          </ContactRecordSub>
                        )}
                      </ItemTextStack>
                    </span>
                    <OpenInNewIcon sx={contactIconSx} />
                  </a>
                </RecordItem>
              );
            })}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const JobsRow = ({ jobs }: { jobs: EmailJob[] }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (jobs.length === 0) {
    return null;
  }
  const preview = jobs[0]?.company ?? '';
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Jobs</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={jobs.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {jobs.map((job, i) => (
              <RecordItem key={i} surface="paper">
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                  }}
                >
                  <BusinessIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      flexShrink: 0,
                      mt: '2px',
                    }}
                  />
                  <ItemTextStack>
                    <ContactRecordTitle>
                      {job.titles[0] ?? job.company}
                    </ContactRecordTitle>
                    {job.titles.length > 0 && (
                      <ContactRecordSub>{job.company}</ContactRecordSub>
                    )}
                  </ItemTextStack>
                </Box>
              </RecordItem>
            ))}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

const EducationRow = ({ education }: { education: string[] }) => {
  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen((open) => !open);
  if (education.length === 0) {
    return null;
  }
  const preview = education[0] ?? '';
  return (
    <AccordionCard>
      <AccordionCardButton
        type="button"
        aria-expanded={open}
        onClick={toggleOpen}
      >
        <ExpandRowMain>
          <ExpandRowLabel>Education</ExpandRowLabel>
          {preview && <ExpandRowPreview>{preview}</ExpandRowPreview>}
        </ExpandRowMain>
        <ExpandRowCount count={education.length} />
        <Chevron open={open} />
      </AccordionCardButton>
      <Collapse in={open}>
        <ExpandPanelInner sx={{ px: 2 }}>
          <ContactRecordList>
            {education.map((edu, i) => (
              <RecordItem key={i} surface="paper">
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'flex-start',
                  }}
                >
                  <SchoolIcon
                    sx={{
                      fontSize: 18,
                      color: 'text.secondary',
                      flexShrink: 0,
                      mt: '2px',
                    }}
                  />
                  <ContactRecordTitle>{edu}</ContactRecordTitle>
                </Box>
              </RecordItem>
            ))}
          </ContactRecordList>
        </ExpandPanelInner>
      </Collapse>
    </AccordionCard>
  );
};

// ─── EmailLinkedRecordsSection ────────────────────────────────────────────────

export const EmailLinkedRecordsSection = ({
  data,
  onSendMessage,
}: {
  data: EmailReportData;
  onSendMessage?: (content: string, displayContent?: string) => void;
}) => (
  <PreviewCardSection title="Linked records">
    <LinkedSection>
      <AddressRows
        addresses={data.potentialAddresses}
        onSendMessage={onSendMessage}
      />
      <EmailRows
        emails={data.potentialEmails}
        currentEmail={data.email}
        onSendMessage={onSendMessage}
      />
      <PhoneRows
        phones={data.potentialPhoneNumbers}
        onSendMessage={onSendMessage}
      />
      <SocialRow profiles={data.socialProfiles} />
      <JobsRow jobs={data.jobs} />
      <EducationRow education={data.education} />
    </LinkedSection>
  </PreviewCardSection>
);
